import React from 'react';
import { CheckCircle, ArrowRight, Users, Settings, Zap } from 'lucide-react';
import { SignupData } from '../SignupForm/SignupForm';

interface WelcomeScreenProps {
  signupData: SignupData;
  onGetStarted: () => void;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ signupData, onGetStarted }) => {
  const nextSteps = [
    {
      icon: <Users className="w-5 h-5 text-blue-600" />,
      title: "Invite Your Team",
      description: "Add team members and assign roles to get everyone on board"
    },
    {
      icon: <Settings className="w-5 h-5 text-blue-600" />,
      title: "Set Up Projects",
      description: "Create your first project and customize your workflow"
    },
    {
      icon: <Zap className="w-5 h-5 text-blue-600" />,
      title: "Start Managing Tasks",
      description: "Create cards, organize sprints, and track progress"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-8 text-center">
        {/* Success Icon */}
        <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
          <CheckCircle className="w-12 h-12 text-green-600" />
        </div>

        {/* Welcome Message */}
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome to KanbanPro, {signupData.user.firstName}!
        </h1>
        <p className="text-lg text-gray-600 mb-8">
          Your organization <span className="font-semibold text-blue-600">{signupData.organization.name}</span> has been successfully created.
        </p>

        {/* Organization Summary */}
        <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
          <h3 className="font-semibold text-gray-900 mb-4">Organization Details</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Organization:</span>
              <p className="font-medium">{signupData.organization.name}</p>
            </div>
            <div>
              <span className="text-gray-500">Industry:</span>
              <p className="font-medium">{signupData.organization.industry}</p>
            </div>
            <div>
              <span className="text-gray-500">Size:</span>
              <p className="font-medium">{signupData.organization.size}</p>
            </div>
            <div>
              <span className="text-gray-500">Admin:</span>
              <p className="font-medium">{signupData.user.firstName} {signupData.user.lastName}</p>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">What's Next?</h3>
          <div className="space-y-4">
            {nextSteps.map((step, index) => (
              <div key={index} className="flex items-start text-left p-4 bg-blue-50 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 bg-white rounded-full flex items-center justify-center mr-4">
                  {step.icon}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{step.title}</h4>
                  <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <button
          onClick={onGetStarted}
          className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors shadow-lg flex items-center justify-center mx-auto"
        >
          Get Started with KanbanPro
          <ArrowRight className="w-5 h-5 ml-2" />
        </button>

        {/* Help Text */}
        <p className="text-sm text-gray-500 mt-6">
          Need help getting started? Check out our{" "}
          <button className="text-blue-600 hover:underline bg-transparent border-none cursor-pointer">
            quick start guide
          </button>{" "}
          or{" "}
          <button className="text-blue-600 hover:underline bg-transparent border-none cursor-pointer">
            contact support
          </button>
          .
        </p>
      </div>
    </div>
  );
};

export default WelcomeScreen;
